import React from 'react';
import COOScoreCard from '../components/COOScoreCard';
import { useTheme } from '../components/ThemeContext';
import { useResponsive } from '../components/useResponsive';

type LookupStatusItem = {
  msg: string;
  type: string;
  time: string;
};

type BatchResultItem = {
  rowIndex: number;
  drugName: string;
  ndc: string;
  status: 'pending' | 'processing' | 'success' | 'error';
  selected: boolean;
  expanded: boolean;
  expandedScrapers: Record<string, boolean>;
  result: any;
  error?: string;
};

type CommitStatus = {
  type: 'success' | 'error';
  message: string;
};

interface IngestionPageProps {
  lookupStatus: LookupStatusItem[];
  batchResults: BatchResultItem[];
  commitStatus: CommitStatus | null;
  setCommitStatus: React.Dispatch<React.SetStateAction<CommitStatus | null>>;
  [key: string]: any;
}

export default function IngestionPage(props: IngestionPageProps) {
  const { T, getConfidenceColor } = useTheme();
  const { isMobile } = useResponsive();
  const {
    SectionHeader,
    ingestionMode,
    setIngestionMode,
    setLookupStatus,
    setIngestionResults,
    setBatchResults,
    AnimatedEagle,
    drugNameInput,
    setDrugNameInput,
    handleNDCLookup,
    ndcInput,
    setNdcInput,
    isIngesting,
    ingestionResults,
    lookupStatus,
    IconSpinner,
    IconBot,
    IconUpload,
    fileInputRef,
    handleFileUpload,
    isBatchProcessing,
    batchResults,
    handleBatchProcess,
    batchProgress,
    toggleAllSelected,
    toggleItemExpanded,
    toggleItemSelected,
    toggleScraperExpanded,
    copyToClipboard,
    handleBatchCommit,
    setRawInput,
    rawInput,
    ingestionUrl,
    setIngestionUrl,
    IconChevron,
    IconCheck,
    confirmIngestion,
    RiskBadge,
    commitStatus,
    setCommitStatus,
  } = props;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <SectionHeader sub="Feed data through AI agents that research, normalize, and capture product intelligence">Data Ingestion Agents</SectionHeader>
      {/* Tabs */}
      <div style={{ display: "flex", flexWrap: isMobile ? "wrap" : undefined, gap: 4, background: T.offWhite, borderRadius: T.radius, padding: 4, width: isMobile ? "100%" : "fit-content", border: `1px solid ${T.border}` }}>
        {[
          { id: "ndc", label: "NDC Lookup" },
          { id: "file", label: "By File" },
          { id: "paste", label: "Paste Data" },
          { id: "url", label: "From URL" },
        ].map(tab => (
          <button key={tab.id} onClick={() => { setIngestionMode(tab.id); setLookupStatus([]); setIngestionResults(null); setBatchResults([]); }} style={{ padding: "10px 22px", borderRadius: T.radiusSm, border: "none", background: ingestionMode === tab.id ? T.white : "transparent", color: ingestionMode === tab.id ? T.navy : T.textLight, fontWeight: 700, fontSize: 13, cursor: "pointer", boxShadow: ingestionMode === tab.id ? T.shadow : "none", transition: "all 0.2s" }}>{tab.label}</button>
        ))}
      </div>

      {/* NDC MODE */}
      {ingestionMode === "ndc" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ background: "linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%)", border: "1px solid #BFDBFE", borderRadius: T.radius, padding: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <AnimatedEagle size={28} />
              <span style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>NDC Lookup Agent</span>
            </div>
            <p style={{ fontSize: 13, color: T.textMid, lineHeight: 1.6 }}>
              Enter an NDC code and/or drug name. The agent searches FDA databases, manufacturer records, import data, and regulatory filings to build a complete profile with relentless focus on <span style={{ color: T.red, fontWeight: 700 }}>country of origin</span> verification.
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Drug Name *</label>
              <input value={drugNameInput} onChange={e => setDrugNameInput(e.target.value)} onKeyDown={e => e.key === "Enter" && handleNDCLookup()} placeholder="e.g., Metformin HCl 500mg Tablets" style={{ width: "100%", padding: "14px 18px", borderRadius: T.radiusSm, border: `2px solid ${T.border}`, background: T.white, color: T.textDark, fontSize: 14, outline: "none", boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>NDC Code (optional)</label>
              <input value={ndcInput} onChange={e => setNdcInput(e.target.value)} onKeyDown={e => e.key === "Enter" && handleNDCLookup()} placeholder="e.g., 00093-1048-01" style={{ width: "100%", padding: "14px 18px", borderRadius: T.radiusSm, border: `2px solid ${T.border}`, background: T.white, color: T.textDark, fontSize: 14, outline: "none", boxSizing: "border-box" }} />
            </div>
          </div>
          <button onClick={handleNDCLookup} disabled={isIngesting || (!drugNameInput.trim() && !ndcInput.trim())} style={{ padding: "14px 32px", borderRadius: T.radius, border: "none", background: `linear-gradient(135deg, ${T.navy}, ${T.navyLight})`, color: T.white, fontWeight: 700, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, width: "fit-content", opacity: isIngesting || (!drugNameInput.trim() && !ndcInput.trim()) ? 0.5 : 1, boxShadow: T.shadowMd }}>
            {isIngesting ? <><IconSpinner /> Agent Researching...</> : <><IconBot /> Launch Lookup Agent</>}
          </button>
          {!isIngesting && !ingestionResults && lookupStatus.length === 0 && (
            <div>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.08em" }}>Try these:</span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                {[
                  { name: "Atorvastatin 40mg Tablets", ndc: "0378-3952", type:"manufactured_in_foreign" },
                  { name: "Children Tylenol", ndc: "50580-555", type:"made in usa" },
                  { name: "StressCalm", ndc: "0220-9333", type:"package_manufactured_in_foreign" },
                  { name: "AMLODIPINE BESYLATE", ndc: "69584-021", type:"package_manufactured_in_usa" },
                  { name: "BISOPROLOL", ndc: "29300-127", type:"package_manufactured_for_by_foreign" },
                  { name: "ACTIMMUNE", ndc: "75987-111", type:"manufactured_by_foreign" },
                  
                ].map(s => (
                  <button key={s.name} onClick={() => { setDrugNameInput(s.name); setNdcInput(s.ndc); }} style={{ padding: "7px 14px", borderRadius: 20, border: `1px solid ${T.border}`, background: T.white, color: T.textMid, fontSize: 11, cursor: "pointer" }}>
                    {s.name}{s.ndc ? ` (${s.ndc})` : ""} {s.type}
                  </button>
                ))}
              </div>
            </div>
          )}
          {lookupStatus.length > 0 && (
            <div style={{ background: T.navy, borderRadius: T.radius, padding: 20, fontFamily: "'IBM Plex Mono', monospace", maxHeight: 260, overflowY: "auto" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: T.gold, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 12 }}>Agent Activity Log</div>
              {lookupStatus.map((s, i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "3px 0", fontSize: 12, lineHeight: 1.5 }}>
                  <span style={{ color: "rgba(255,255,255,0.25)", flexShrink: 0, fontSize: 10, marginTop: 2 }}>{s.time}</span>
                  <span style={{ color: s.type === "success" ? "#4ADE80" : s.type === "error" ? "#F87171" : s.type === "warning" ? "#FBBF24" : s.type === "search" ? "#38BDF8" : "rgba(255,255,255,0.5)" }}>
                    {s.msg}
                  </span>
                </div>
              ))}
              {isIngesting && <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, color: "#38BDF8", fontSize: 12 }}><IconSpinner /> Agent working...</div>}
            </div>
          )}
        </div>
      )}

      {/* FILE MODE - Batch Processing */}
      {ingestionMode === "file" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ background: "linear-gradient(135deg, #FEF3C7 0%, #FEF9C3 100%)", border: "1px solid #FCD34D", borderRadius: T.radius, padding: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <IconUpload />
              <span style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>Batch File Import</span>
            </div>
            <p style={{ fontSize: 13, color: T.textMid, lineHeight: 1.6 }}>
              Upload a CSV or text file with one product per line. Format: <code style={{ background: "rgba(0,0,0,0.05)", padding: "2px 6px", borderRadius: 4 }}>name;ndc</code> or <code style={{ background: "rgba(0,0,0,0.05)", padding: "2px 6px", borderRadius: 4 }}>ndc</code> per line.
              The same scrapers (FDA, Shortage, COO, NADAC) will run for each entry.
            </p>
          </div>

          {/* File Upload */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt,.tsv"
              onChange={handleFileUpload}
              style={{ display: "none" }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isBatchProcessing}
              style={{ padding: "14px 32px", borderRadius: T.radius, border: `2px dashed ${T.border}`, background: T.white, color: T.navy, fontWeight: 700, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}
            >
              <IconUpload /> Choose File (.csv, .txt)
            </button>
            {batchResults.length > 0 && !isBatchProcessing && (
              <>
                <button
                  onClick={handleBatchProcess}
                  style={{ padding: "14px 32px", borderRadius: T.radius, border: "none", background: `linear-gradient(135deg, ${T.navy}, ${T.navyLight})`, color: T.white, fontWeight: 700, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, boxShadow: T.shadowMd }}
                >
                  <IconBot /> Process {batchResults.length} Items
                </button>
                <button
                  onClick={() => setBatchResults([])}
                  style={{ padding: "14px 20px", borderRadius: T.radius, border: `1px solid ${T.border}`, background: T.white, color: T.textMid, fontWeight: 600, fontSize: 13, cursor: "pointer" }}
                >
                  Clear
                </button>
              </>
            )}
          </div>

          {/* Progress Bar */}
          {isBatchProcessing && (
            <div style={{ background: T.offWhite, borderRadius: T.radius, padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: T.textMid }}>Processing...</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: T.navy }}>{batchProgress.current} / {batchProgress.total}</span>
              </div>
              <div style={{ background: T.border, borderRadius: 4, height: 8, overflow: "hidden" }}>
                <div style={{ background: `linear-gradient(90deg, ${T.navy}, ${T.gold})`, height: "100%", width: `${(batchProgress.current / batchProgress.total) * 100}%`, transition: "width 0.3s" }} />
              </div>
            </div>
          )}

          {/* Batch Results List */}
          {batchResults.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {/* Header with Select All */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", background: T.navy, borderRadius: T.radiusSm }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <input
                    type="checkbox"
                    checked={batchResults.every(r => r.selected)}
                    onChange={(e) => toggleAllSelected(e.target.checked)}
                    style={{ width: 18, height: 18, cursor: "pointer" }}
                  />
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.white }}>
                    {batchResults.filter(r => r.selected).length} / {batchResults.length} selected
                  </span>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => toggleAllSelected(true)} style={{ padding: "6px 12px", borderRadius: 4, border: "none", background: "rgba(255,255,255,0.1)", color: T.white, fontSize: 11, cursor: "pointer" }}>Select All</button>
                  <button onClick={() => toggleAllSelected(false)} style={{ padding: "6px 12px", borderRadius: 4, border: "none", background: "rgba(255,255,255,0.1)", color: T.white, fontSize: 11, cursor: "pointer" }}>Unselect All</button>
                </div>
              </div>

              {/* Results List */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {batchResults.map((item, index) => (
                  <div key={index} style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radiusSm, overflow: "hidden" }}>
                    {/* Row Header */}
                    <div
                      style={{
                        display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", cursor: "pointer",
                        background: item.status === 'processing' ? T.infoBg : item.status === 'success' ? T.successBg : item.status === 'error' ? T.dangerBg : T.white
                      }}
                      onClick={() => item.result && toggleItemExpanded(index)}
                    >
                      <input
                        type="checkbox"
                        checked={item.selected}
                        onChange={() => toggleItemSelected(index)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: 16, height: 16, cursor: "pointer" }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.textDark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {item.drugName || item.ndc || `Row ${index + 1}`}
                        </div>
                        <div style={{ fontSize: 11, color: T.textLight }}>
                          NDC: {item.ndc || "N/A"}
                        </div>
                      </div>
                      {/* Status Badge */}
                      <div style={{
                        padding: "4px 10px", borderRadius: 12, fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                        background: item.status === 'pending' ? T.offWhite :
                                   item.status === 'processing' ? T.infoBg :
                                   item.status === 'success' ? T.successBg : T.dangerBg,
                        color: item.status === 'pending' ? T.textLight :
                               item.status === 'processing' ? "#2563EB" :
                               item.status === 'success' ? T.success : T.danger
                      }}>
                        {item.status === 'processing' && <IconSpinner />}
                        {item.status}
                      </div>
                      {/* Expand Arrow */}
                      {item.result && (
                        <div style={{ transform: item.expanded ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s", color: T.textLight }}>
                          <IconChevron />
                        </div>
                      )}
                    </div>

                    {/* Expanded Content - Reuse same display as single mode */}
                    {item.expanded && item.result && (
                      <div style={{ borderTop: `1px solid ${T.border}`, padding: 16, background: T.offWhite }}>
                        {/* Summary */}
                        <div style={{ display: "flex", gap: 16, marginBottom: 12, fontSize: 11 }}>
                          <span><strong>Batch:</strong> {item.result.batch_id}</span>
                          <span><strong>API Calls:</strong> {item.result.summary?.total_api_calls}</span>
                          <span><strong>Duration:</strong> {item.result.summary?.total_duration_seconds}s</span>
                        </div>
                        {/* Scrapers with dropdown details */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          {item.result.scrapers?.map((scraper: any, si: number) => (
                            <div key={si} style={{ border: `1px solid ${scraper.success ? "#BBF7D0" : "#FECACA"}`, borderRadius: T.radiusSm, overflow: "hidden" }}>
                              {/* Scraper Header - Clickable */}
                              <div
                                onClick={() => toggleScraperExpanded(index, scraper.source)}
                                style={{
                                  display: "flex", alignItems: "center", justifyContent: "space-between",
                                  padding: "10px 14px", cursor: "pointer",
                                  background: scraper.success ? T.successBg : T.dangerBg,
                                }}
                              >
                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                  <span style={{ fontSize: 12, fontWeight: 700, color: scraper.success ? T.success : T.danger }}>
                                    {scraper.success ? "✓" : "✗"} {scraper.scraper_name}
                                  </span>
                                  <span style={{ fontSize: 10, color: T.textLight }}>({scraper.duration_seconds}s)</span>
                                </div>
                                <div style={{ transform: item.expandedScrapers[scraper.source] ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s", color: T.textLight }}>
                                  <IconChevron />
                                </div>
                              </div>
                              {/* Scraper Details - Expanded */}
                              {item.expandedScrapers[scraper.source] && (
                                <div style={{ padding: 14, background: T.white, fontSize: 11 }}>
                                  {/* FDA NDC Data */}
                                  {scraper.source === "FDA_NDC" && scraper.data && (
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                                      <div><span style={{ color: T.textLight }}>Product:</span> <strong>{scraper.data.product_name || "N/A"}</strong></div>
                                      <div><span style={{ color: T.textLight }}>Labeler:</span> <strong>{scraper.data.labeler_name || "N/A"}</strong></div>
                                      <div><span style={{ color: T.textLight }}>NDC:</span> <strong>{scraper.data.product_ndc || "N/A"}</strong></div>
                                      <div><span style={{ color: T.textLight }}>Dosage:</span> <strong>{scraper.data.dosage_form || "N/A"}</strong></div>
                                      <div><span style={{ color: T.textLight }}>Route:</span> <strong>{scraper.data.route || "N/A"}</strong></div>
                                      <div><span style={{ color: T.textLight }}>Type:</span> <strong>{scraper.data.product_type || "N/A"}</strong></div>
                                      {scraper.data.packaging && scraper.data.packaging.length > 0 && (
                                        <div style={{ gridColumn: "1 / -1", marginTop: 8 }}>
                                          <span style={{ color: T.textLight }}>Packagings:</span> <strong>{scraper.data.packaging.length}</strong>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {/* FDA Shortage Data */}
                                  {scraper.source === "FDA_SHORTAGE" && scraper.data && (
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                                      <div><span style={{ color: T.textLight }}>Status:</span> <strong style={{ color: scraper.data.status === "Current" ? T.danger : T.success }}>{scraper.data.status || "No shortage"}</strong></div>
                                      {scraper.data.shortage_reason && <div style={{ gridColumn: "1 / -1" }}><span style={{ color: T.textLight }}>Reason:</span> <strong>{scraper.data.shortage_reason}</strong></div>}
                                    </div>
                                  )}
                                  {/* COO Data */}
                                  {scraper.source === "COO_SCRAPER" && scraper.data && (
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                                      <div>
                                        <span style={{ color: T.textLight }}>Country:</span>{" "}
                                        <strong style={{ color: scraper.data.country === "United States" ? T.success : scraper.data.country === "China" ? T.danger : scraper.data.country ? T.warning : T.textLight }}>
                                          {scraper.data.country || "Not Found"}
                                        </strong>
                                      </div>
                                      <div><span style={{ color: T.textLight }}>Confidence:</span> <strong>{scraper.data.confidence || "N/A"}</strong></div>
                                      <div><span style={{ color: T.textLight }}>Source:</span> <strong>{scraper.data.source_type || "N/A"}</strong></div>
                                      <div><span style={{ color: T.textLight }}>Score:</span> <strong>{scraper.data.scoring_final !== undefined && scraper.data.scoring_final !== null ? scraper.data.scoring_final : "N/A"}%</strong></div>
                                      <div><span style={{ color: T.textLight }}>Tier:</span> <strong>{scraper.data.scoring_tier || "N/A"}</strong></div>
                                      <div><span style={{ color: T.textLight }}>Evidences:</span> <strong>{scraper.data.evidences_count !== undefined && scraper.data.evidences_count !== null ? scraper.data.evidences_count : (Array.isArray(scraper.data.evidences) ? scraper.data.evidences.length : 0)}</strong></div>
                                      {scraper.data.deduction && <div style={{ gridColumn: "1 / -1" }}><span style={{ color: T.textLight }}>Deduction:</span> <strong>{scraper.data.deduction}</strong></div>}
                                    </div>
                                  )}
                                  {/* NADAC Data */}
                                  {scraper.source === "CMS_NADAC" && scraper.data && (
                                    <div>
                                      {scraper.data.packaging_prices && scraper.data.packaging_prices.length > 0 ? (
                                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                          {scraper.data.packaging_prices.slice(0, 5).map((pkg: any, pi: number) => (
                                            <div key={pi} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: T.offWhite, borderRadius: 4 }}>
                                              <div>
                                                <div style={{ fontWeight: 600 }}>{pkg.fda_package_ndc || pkg.ndc_11}</div>
                                                {pkg.ndc_description && <div style={{ fontSize: 10, color: T.textLight, marginTop: 2 }}>{pkg.ndc_description}</div>}
                                              </div>
                                              <div style={{ textAlign: "right" }}>
                                                <strong style={{ color: T.success }}>${pkg.nadac_per_unit?.toFixed(4)}</strong>
                                                <div style={{ fontSize: 9, color: T.textLight }}>/{pkg.pricing_unit || "unit"}</div>
                                              </div>
                                            </div>
                                          ))}
                                          {scraper.data.packaging_prices.length > 5 && <span style={{ color: T.textLight, fontSize: 10 }}>+{scraper.data.packaging_prices.length - 5} more...</span>}
                                        </div>
                                      ) : (
                                        <span style={{ color: T.textLight }}>{scraper.data.message || "No pricing data"}</span>
                                      )}
                                    </div>
                                  )}
                                  {/* RxNorm Data */}
                                  {scraper.source === "RXNORM" && scraper.data && (
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                                      {scraper.data.clinical_drug_name ? (
                                        <>
                                          <div style={{ gridColumn: "1 / -1" }}>
                                            <span style={{ color: T.textLight }}>Clinical Drug:</span>{" "}
                                            <strong style={{ color: T.navy }}>{scraper.data.clinical_drug_name}</strong>
                                          </div>
                                          <div>
                                            <span style={{ color: T.textLight }}>SCD RxCUI:</span>{" "}
                                            <strong style={{ color: "#2563EB" }}>{scraper.data.scd_rxcui}</strong>
                                          </div>
                                          {scraper.data.sbd_rxcui && (
                                            <div>
                                              <span style={{ color: T.textLight }}>SBD RxCUI:</span>{" "}
                                              <strong style={{ color: "#7C3AED" }}>{scraper.data.sbd_rxcui}</strong>
                                            </div>
                                          )}
                                          {scraper.data.ingredient_name && (
                                            <div>
                                              <span style={{ color: T.textLight }}>Ingredient:</span>{" "}
                                              <strong>{scraper.data.ingredient_name}</strong>
                                            </div>
                                          )}
                                          {scraper.data.dose_form && (
                                            <div>
                                              <span style={{ color: T.textLight }}>Dose Form:</span>{" "}
                                              <strong>{scraper.data.dose_form}</strong>
                                            </div>
                                          )}
                                          {scraper.data.sbd_name && (
                                            <div style={{ gridColumn: "1 / -1", marginTop: 4, padding: 8, background: "#F3E8FF", borderRadius: 4, border: "1px solid #C4B5FD" }}>
                                              <span style={{ fontSize: 10, color: "#6D28D9" }}>Branded: {scraper.data.sbd_name}</span>
                                            </div>
                                          )}
                                        </>
                                      ) : (
                                        <span style={{ color: T.textLight, gridColumn: "1 / -1" }}>{scraper.data.message || "No RxNorm data found"}</span>
                                      )}
                                    </div>
                                  )}
                                  {/* Error message */}
                                  {!scraper.success && scraper.error && (
                                    <div style={{ color: T.danger }}>{scraper.error}</div>
                                  )}
                                  {/* API Calls */}
                                  {scraper.api_calls && scraper.api_calls.length > 0 && (
                                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
                                      <div style={{ fontSize: 9, fontWeight: 700, color: T.textLight, textTransform: "uppercase", marginBottom: 6 }}>API Calls</div>
                                      {scraper.api_calls.map((call: any, ci: number) => (
                                        <div key={ci} style={{ fontSize: 9, color: T.textMid, marginBottom: 2, wordBreak: "break-all", cursor: "pointer" }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const target = e.currentTarget;
                                            copyToClipboard(call.endpoint || "").then(() => {
                                              if (target) {
                                                target.style.color = T.success;
                                                setTimeout(() => { target.style.color = T.textMid; }, 300);
                                              }
                                            });
                                          }}
                                          title="Click to copy">
                                          <span style={{ color: T.success }}>{call.method}</span> {call.endpoint}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Commit Button */}
              {batchResults.some(r => r.status === 'success' && r.selected) && !isBatchProcessing && (
                <button
                  onClick={handleBatchCommit}
                  style={{ padding: "14px 32px", borderRadius: T.radius, border: "none", background: `linear-gradient(135deg, ${T.success}, #10B981)`, color: T.white, fontWeight: 700, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, width: "fit-content", boxShadow: T.shadowMd, marginTop: 8 }}
                >
                  <IconCheck /> Commit {batchResults.filter(r => r.selected && r.status === 'success').length} Selected to Database
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* PASTE MODE */}
      {ingestionMode === "paste" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <textarea value={rawInput} onChange={e => setRawInput(e.target.value)} placeholder={"Paste any product data here - CSV, JSON, table text, regulatory filing excerpts...\n\nExample:\nMetformin HCl 500mg, Teva, NDC 00093-1048-01, manufactured in Sellersville PA, API from India"} style={{ minHeight: 200, padding: 18, borderRadius: T.radius, border: `2px solid ${T.border}`, background: T.white, color: T.textDark, fontSize: 13, fontFamily: "'IBM Plex Mono', monospace", outline: "none", resize: "vertical", lineHeight: 1.6 }} />
          <button onClick={() => {}} disabled={isIngesting || !rawInput.trim()} style={{ padding: "12px 28px", borderRadius: T.radius, border: "none", background: T.navy, color: T.white, fontWeight: 700, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, width: "fit-content", opacity: isIngesting || !rawInput.trim() ? 0.5 : 1 }}>
            {isIngesting ? <><IconSpinner /> Processing...</> : <><IconBot /> Run Ingestion Agent</>}
          </button>
        </div>
      )}

      {/* URL MODE */}
      {ingestionMode === "url" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <input value={ingestionUrl} onChange={e => setIngestionUrl(e.target.value)} placeholder="Paste a URL to a product page, FDA database, manufacturer catalog..." style={{ padding: "14px 18px", borderRadius: T.radiusSm, border: `2px solid ${T.border}`, background: T.white, color: T.textDark, fontSize: 14, outline: "none" }} />
          <button onClick={() => {}} disabled={isIngesting || !ingestionUrl.trim()} style={{ padding: "12px 28px", borderRadius: T.radius, border: "none", background: T.navy, color: T.white, fontWeight: 700, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, width: "fit-content", opacity: isIngesting || !ingestionUrl.trim() ? 0.5 : 1 }}>
            {isIngesting ? <><IconSpinner /> Fetching...</> : <><IconBot /> Fetch & Ingest</>}
          </button>
        </div>
      )}

      {/* Error */}
      {ingestionResults === "error" && (
        <div style={{ background: T.dangerBg, border: "1px solid #FECACA", borderRadius: T.radius, padding: 16, color: T.danger, fontSize: 13 }}>
          Ingestion failed. Try providing more structured data or additional context.
        </div>
      )}

      {/* Scraper Results - New multi-card display */}
      {ingestionResults && ingestionResults.type === "scraper_results" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Summary Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", background: T.offWhite, border: `1px solid ${T.border}`, borderRadius: T.radius }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <AnimatedEagle size={28} />
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: T.textDark }}>{ingestionResults.drug_name}</div>
                  <div style={{ fontSize: 12, color: T.textMid }}>NDC: {ingestionResults.ndc_code || "Auto-detected"} • Batch: {ingestionResults.batch_id}</div>
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => { setIngestionResults(null); setLookupStatus([]); setCommitStatus(null); }} style={{ padding: "8px 18px", borderRadius: T.radiusSm, border: `1px solid ${T.border}`, background: "transparent", color: T.textDark, fontSize: 12, cursor: "pointer", fontWeight: 600 }}>Discard</button>
              <button onClick={confirmIngestion} disabled={!!commitStatus} style={{ padding: "8px 18px", borderRadius: T.radiusSm, border: "none", background: commitStatus ? (commitStatus.type === 'success' ? T.success : T.danger) : T.gold, color: commitStatus ? T.white : T.navy, fontSize: 12, fontWeight: 700, cursor: commitStatus ? "default" : "pointer", opacity: commitStatus ? 0.9 : 1 }}>
                {commitStatus ? (commitStatus.type === 'success' ? '✓ Saved!' : '✗ Error') : 'Save to Database'}
              </button>
            </div>
          </div>

          {/* Commit Status Message */}
          {commitStatus && (
            <div style={{
              padding: "12px 16px",
              borderRadius: T.radiusSm,
              background: commitStatus.type === 'success' ? '#DCFCE7' : '#FEF2F2',
              border: `1px solid ${commitStatus.type === 'success' ? '#BBF7D0' : '#FECACA'}`,
              color: commitStatus.type === 'success' ? T.success : T.danger,
              fontSize: 13,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 8
            }}>
              <span style={{ fontSize: 16 }}>{commitStatus.type === 'success' ? '✓' : '✗'}</span>
              {commitStatus.message}
            </div>
          )}

          {/* Existing Product Banner */}
          {ingestionResults.existing_product ? (
            <div style={{
              padding: "16px 20px",
              borderRadius: T.radius,
              background: `linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)`,
              border: `1px solid #F59E0B`,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 18 }}>📦</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#92400E" }}>PRODUCT EXISTS IN DATABASE</span>
                <span style={{ fontSize: 11, color: "#B45309", marginLeft: "auto" }}>ID: #{ingestionResults.existing_product.id}</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, fontSize: 12 }}>
                <div>
                  <span style={{ color: "#92400E" }}>Current Name:</span>
                  <div style={{ fontWeight: 600, color: "#78350F" }}>{ingestionResults.existing_product.product_name || "—"}</div>
                </div>
                <div>
                  <span style={{ color: "#92400E" }}>Current COO:</span>
                  <div style={{ fontWeight: 600, color: ingestionResults.existing_product.country_of_origin ? "#78350F" : "#B45309" }}>
                    {ingestionResults.existing_product.country_of_origin || "Not set"}
                    {ingestionResults.existing_product.coo_confidence && <span style={{ fontSize: 10, marginLeft: 4, opacity: 0.7 }}>({ingestionResults.existing_product.coo_confidence})</span>}
                  </div>
                </div>
                <div>
                  <span style={{ color: "#92400E" }}>Manufacturer:</span>
                  <div style={{ fontWeight: 600, color: "#78350F" }}>{ingestionResults.existing_product.manufacturer_name || "—"}</div>
                </div>
              </div>
              {/* Show what will change */}
              {(() => {
                const existing = ingestionResults.existing_product;
                const newCoo = ingestionResults.scrapers.find((s: any) => s.source === "COO_SCRAPER")?.data?.country;
                const newProduct = ingestionResults.scrapers.find((s: any) => s.source === "FDA_NDC")?.data;
                const changes: string[] = [];

                if (newCoo && newCoo !== existing.country_of_origin) {
                  changes.push(`COO: "${existing.country_of_origin || 'None'}" → "${newCoo}"`);
                }
                if (newProduct?.product_name && newProduct.product_name !== existing.product_name) {
                  changes.push(`Name update`);
                }
                if (newProduct?.dosage_form && newProduct.dosage_form !== existing.dosage_form) {
                  changes.push(`Dosage form update`);
                }

                if (changes.length > 0) {
                  return (
                    <div style={{ marginTop: 12, padding: "10px 14px", background: "rgba(255,255,255,0.6)", borderRadius: T.radiusSm }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#92400E", marginBottom: 4 }}>CHANGES TO APPLY:</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {changes.map((c, i) => (
                          <span key={i} style={{ padding: "4px 10px", background: "#22C55E", color: "white", borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{c}</span>
                        ))}
                      </div>
                    </div>
                  );
                }
                return (
                  <div style={{ marginTop: 12, padding: "10px 14px", background: "rgba(255,255,255,0.6)", borderRadius: T.radiusSm, display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 14 }}>✓</span>
                    <span style={{ fontSize: 12, color: "#92400E", fontWeight: 600 }}>No new data to update — product is already up to date</span>
                  </div>
                );
              })()}
            </div>
          ) : (
            <div style={{
              padding: "12px 16px",
              borderRadius: T.radiusSm,
              background: `linear-gradient(135deg, #DCFCE7 0%, #BBF7D0 100%)`,
              border: `1px solid #22C55E`,
              display: "flex",
              alignItems: "center",
              gap: 8
            }}>
              <span style={{ fontSize: 16 }}>✨</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#166534" }}>NEW PRODUCT — Will be added to database</span>
            </div>
          )}

          {/* Summary Stats */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
            <div style={{ padding: 16, background: T.white, borderRadius: T.radiusSm, border: `1px solid ${T.border}`, textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: T.navy }}>{ingestionResults.summary.successful}/{ingestionResults.summary.total_scrapers}</div>
              <div style={{ fontSize: 11, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.05em" }}>Successful</div>
            </div>
            <div style={{ padding: 16, background: T.white, borderRadius: T.radiusSm, border: `1px solid ${T.border}`, textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: T.success }}>{ingestionResults.summary.staged}</div>
              <div style={{ fontSize: 11, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.05em" }}>Staged</div>
            </div>
            <div style={{ padding: 16, background: T.white, borderRadius: T.radiusSm, border: `1px solid ${T.border}`, textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: T.navyLight }}>{ingestionResults.summary.total_api_calls}</div>
              <div style={{ fontSize: 11, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.05em" }}>API Calls</div>
            </div>
            <div style={{ padding: 16, background: T.white, borderRadius: T.radiusSm, border: `1px solid ${T.border}`, textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: T.gold }}>{ingestionResults.summary.total_duration_seconds}s</div>
              <div style={{ fontSize: 11, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.05em" }}>Duration</div>
            </div>
          </div>

          {/* Individual Scraper Cards */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16 }}>
            {ingestionResults.scrapers.map((scraper: any, i: number) => (
              <div key={i} style={{
                padding: 20,
                background: T.white,
                border: `1px solid ${scraper.success ? (scraper.staged ? "#BBF7D0" : T.border) : "#FECACA"}`,
                borderRadius: T.radius,
                boxShadow: T.shadow
              }}>
                {/* Scraper Header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 8,
                      background: scraper.success ? (scraper.staged ? "#DCFCE7" : T.offWhite) : "#FEF2F2",
                      display: "flex", alignItems: "center", justifyContent: "center"
                    }}>
                      {scraper.source === "FDA_NDC" && <span style={{ fontSize: 18 }}>💊</span>}
                      {scraper.source === "FDA_SHORTAGE" && <span style={{ fontSize: 18 }}>⚠️</span>}
                      {scraper.source === "COO_SCRAPER" && <span style={{ fontSize: 18 }}>🌍</span>}
                      {scraper.source === "CMS_NADAC" && <span style={{ fontSize: 18 }}>💰</span>}
                      {scraper.source === "RXNORM" && <span style={{ fontSize: 18 }}>🧬</span>}
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: T.textDark }}>{scraper.scraper_name}</div>
                      <div style={{ fontSize: 11, color: T.textLight }}>Source: {scraper.source} • {scraper.duration_seconds}s</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {scraper.success ? (
                      <>
                        {scraper.staged && (
                          <span style={{ padding: "4px 8px", borderRadius: 4, background: "#DCFCE7", color: T.success, fontSize: 10, fontWeight: 700 }}>STAGED #{scraper.staging_id}</span>
                        )}
                        <span style={{ padding: "4px 8px", borderRadius: 4, background: "#DCFCE7", color: T.success, fontSize: 10, fontWeight: 700 }}>SUCCESS</span>
                      </>
                    ) : (
                      <span style={{ padding: "4px 8px", borderRadius: 4, background: "#FEF2F2", color: T.danger, fontSize: 10, fontWeight: 700 }}>FAILED</span>
                    )}
                  </div>
                </div>

                {/* API Calls — hidden for COO_SCRAPER (detail already in evidences/deduction) */}
                {scraper.api_calls && scraper.api_calls.length > 0 && scraper.source !== "COO_SCRAPER" && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>API Calls ({scraper.api_calls.length})</div>
                    <div style={{ background: T.navy, borderRadius: T.radiusSm, padding: 10, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, maxHeight: 120, overflowY: "auto" }}>
                      {scraper.api_calls.map((call: any, j: number) => (
                        <div key={j} style={{ color: "#38BDF8", marginBottom: 6, wordBreak: "break-all", cursor: "pointer" }}
                          onClick={(e) => {
                            const target = e.currentTarget; // Capture BEFORE async
                            const url = call.endpoint || "";
                            copyToClipboard(url).then(() => {
                              // Visual feedback - flash green
                              if (target) {
                                target.style.color = "#4ADE80";
                                setTimeout(() => { target.style.color = "#38BDF8"; }, 300);
                              }
                            });
                          }}
                          title="Click to copy URL">
                          <span style={{ color: "#4ADE80" }}>{call.method || "GET"}</span> {call.endpoint}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Data Display */}
                {scraper.success && scraper.data && (
                  <div style={{ background: T.offWhite, borderRadius: T.radiusSm, padding: 12 }}>
                    {/* FDA NDC Data */}
                    {scraper.source === "FDA_NDC" && (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 12 }}>
                        <div><span style={{ color: T.textLight }}>Product:</span> <strong style={{ color: T.textDark }}>{scraper.data.product_name || "N/A"}</strong></div>
                        <div><span style={{ color: T.textLight }}>NDC:</span> <strong style={{ color: T.navy }}>{scraper.data.ndc_code || "N/A"}</strong></div>
                        <div><span style={{ color: T.textLight }}>Labeler:</span> <strong>{scraper.data.labeler_name || "N/A"}</strong></div>
                        <div><span style={{ color: T.textLight }}>Dosage:</span> <strong>{scraper.data.dosage_form || "N/A"}</strong></div>
                        <div><span style={{ color: T.textLight }}>Route:</span> <strong>{scraper.data.route || "N/A"}</strong></div>
                        <div><span style={{ color: T.textLight }}>Type:</span> <strong>{scraper.data.product_type || "N/A"}</strong></div>
                        {scraper.data.pharm_class && <div style={{ gridColumn: "1 / -1" }}><span style={{ color: T.textLight }}>Class:</span> <strong>{scraper.data.pharm_class}</strong></div>}
                      </div>
                    )}

                    {/* FDA Shortage Data */}
                    {scraper.source === "FDA_SHORTAGE" && (
                      <div>
                        {scraper.data.status === "no_shortage" ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 8, color: T.success }}>
                            <IconCheck />
                            <span style={{ fontWeight: 600 }}>{scraper.data.message}</span>
                          </div>
                        ) : (
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 12 }}>
                            <div><span style={{ color: T.textLight }}>Product:</span> <strong>{scraper.data.generic_name || "N/A"}</strong></div>
                            <div><span style={{ color: T.textLight }}>Status:</span> <strong style={{ color: T.danger }}>{scraper.data.status || "Unknown"}</strong></div>
                            <div style={{ gridColumn: "1 / -1" }}><span style={{ color: T.textLight }}>Availability:</span> <strong>{scraper.data.availability || "N/A"}</strong></div>
                            {scraper.data.therapeutic_category && <div style={{ gridColumn: "1 / -1" }}><span style={{ color: T.textLight }}>Category:</span> <strong>{scraper.data.therapeutic_category}</strong></div>}
                          </div>
                        )}
                      </div>
                    )}

                    {/* COO Data */}
                    {scraper.source === "COO_SCRAPER" && (() => {
                      const coo = scraper.data;
                      const sc = coo.scoring;
                      return (
                        <COOScoreCard
                          country={coo.country || null}
                          confidence={coo.confidence || null}
                          finalScore={coo.scoring_final ?? null}
                          tier={coo.scoring_tier || null}
                          productName={coo.product_name || null}
                          scoring={sc ? {
                            baseline:       sc.baseline ?? null,
                            baselineReason: sc.baseline_reason || null,
                            rawScore:       sc.raw_score ?? null,
                            categoriesCount:sc.categories_count ?? null,
                            adjustments:    sc.adjustments || [],
                            ceilingsApplied:sc.ceilings_applied || [],
                            contradictions: sc.contradictions || [],
                          } : null}
                          evidences={(coo.evidences || []).map((ev: any) => ({
                            source:             ev.source || "Unknown",
                            country:            ev.country || null,
                            confidence:         ev.confidence || "very_low",
                            score:              ev._score ?? null,
                            matchText:          ev.raw_match_text || null,
                            validationNote:     ev.validation_note || null,
                            deductionReasoning: ev.deduction_reasoning || null,
                          }))}
                          primaryDeduction={coo.deduction || null}
                          note={coo.note || null}
                        />
                      );
                    })()}

                    {/* CMS NADAC Pricing Data - All Packagings */}
                    {scraper.source === "CMS_NADAC" && scraper.data && (
                      <div>
                        {scraper.data.status === "not_found" || scraper.data.total_packagings === 0 ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 8, color: T.textLight }}>
                            <span>ℹ️</span>
                            <span style={{ fontWeight: 600 }}>{scraper.data.message || "No NADAC pricing data found"}</span>
                          </div>
                        ) : (
                          <div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", marginBottom: 12 }}>
                              {scraper.data.total_packagings} Packaging{scraper.data.total_packagings > 1 ? "s" : ""} with NADAC Pricing
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                              {scraper.data.packaging_prices?.map((pkg: any, idx: number) => (
                                <div key={idx} style={{ padding: "12px 16px", background: `linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)`, borderRadius: T.radiusSm }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                                    <div>
                                      <div style={{ fontSize: 10, fontWeight: 700, color: "#92400E", textTransform: "uppercase", marginBottom: 2 }}>
                                        NDC: {pkg.ndc_display || pkg.ndc_11}
                                      </div>
                                      <div style={{ fontSize: 11, color: "#78350F" }}>{pkg.ndc_description || "N/A"}</div>
                                    </div>
                                    <div style={{ textAlign: "right" }}>
                                      <div style={{ fontSize: 20, fontWeight: 800, color: "#78350F" }}>
                                        ${pkg.nadac_per_unit?.toFixed(4) || "N/A"}
                                      </div>
                                      <div style={{ fontSize: 10, color: "#92400E" }}>per {pkg.pricing_unit || "unit"}</div>
                                    </div>
                                  </div>
                                  <div style={{ display: "flex", gap: 16, fontSize: 10, color: "#92400E" }}>
                                    <span>Effective: {pkg.effective_date || "N/A"}</span>
                                    <span>Class: {pkg.classification === "G" ? "Generic" : pkg.classification === "B" ? "Brand" : pkg.classification || "N/A"}</span>
                                    <span>OTC: {pkg.otc_indicator === "Y" ? "Yes" : "No"}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* RxNorm Clinical Drug Data */}
                    {scraper.source === "RXNORM" && scraper.data && (
                      <div>
                        {scraper.data.status === "not_found" || !scraper.data.clinical_drug_name ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 8, color: T.textLight }}>
                            <span>ℹ️</span>
                            <span style={{ fontWeight: 600 }}>{scraper.data.message || "No RxNorm data found"}</span>
                          </div>
                        ) : (
                          <div>
                            {/* Clinical Drug Header */}
                            <div style={{ padding: "16px 20px", background: `linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)`, borderRadius: T.radiusSm, marginBottom: 12 }}>
                              <div style={{ fontSize: 10, fontWeight: 700, color: "#6D28D9", textTransform: "uppercase", marginBottom: 6 }}>
                                Clinical Drug (SCD)
                              </div>
                              <div style={{ fontSize: 15, fontWeight: 700, color: "#4C1D95" }}>
                                {scraper.data.clinical_drug_name}
                              </div>
                              <div style={{ fontSize: 12, color: "#7C3AED", marginTop: 4 }}>
                                RxCUI: {scraper.data.scd_rxcui}
                              </div>
                            </div>

                            {/* Details Grid */}
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                              {scraper.data.sbd_rxcui && (
                                <div style={{ padding: 12, background: "#FDF4FF", borderRadius: T.radiusSm, border: "1px solid #E9D5FF" }}>
                                  <div style={{ fontSize: 10, fontWeight: 700, color: "#A855F7", textTransform: "uppercase", marginBottom: 4 }}>Branded Drug (SBD)</div>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: "#7E22CE" }}>{scraper.data.sbd_name || "N/A"}</div>
                                  <div style={{ fontSize: 10, color: "#A855F7" }}>RxCUI: {scraper.data.sbd_rxcui}</div>
                                </div>
                              )}
                              {scraper.data.ingredient_name && (
                                <div style={{ padding: 12, background: "#F0FDF4", borderRadius: T.radiusSm, border: "1px solid #BBF7D0" }}>
                                  <div style={{ fontSize: 10, fontWeight: 700, color: T.success, textTransform: "uppercase", marginBottom: 4 }}>Ingredient</div>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: "#166534" }}>{scraper.data.ingredient_name}</div>
                                  {scraper.data.ingredient_rxcui && <div style={{ fontSize: 10, color: T.success }}>RxCUI: {scraper.data.ingredient_rxcui}</div>}
                                </div>
                              )}
                              {scraper.data.dose_form && (
                                <div style={{ padding: 12, background: T.offWhite, borderRadius: T.radiusSm, border: `1px solid ${T.border}` }}>
                                  <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", marginBottom: 4 }}>Dose Form</div>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: T.textDark }}>{scraper.data.dose_form}</div>
                                </div>
                              )}
                              {scraper.data.strength && (
                                <div style={{ padding: 12, background: T.offWhite, borderRadius: T.radiusSm, border: `1px solid ${T.border}` }}>
                                  <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", marginBottom: 4 }}>Strength</div>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: T.textDark }}>{scraper.data.strength}</div>
                                </div>
                              )}
                            </div>

                            {/* Info Banner */}
                            <div style={{ marginTop: 12, padding: 10, background: "#EFF6FF", borderRadius: T.radiusSm, border: "1px solid #BFDBFE" }}>
                              <div style={{ fontSize: 10, color: "#1E40AF" }}>
                                💡 Products with the same Clinical Drug ID share the same therapeutic equivalence. This enables finding generic alternatives.
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Error Message */}
                {!scraper.success && scraper.error && (
                  <div style={{ padding: 12, background: "#FEF2F2", borderRadius: T.radiusSm, color: T.danger, fontSize: 12 }}>
                    <strong>Error:</strong> {scraper.error}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Legacy Results - Array format */}
      {ingestionResults && Array.isArray(ingestionResults) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: T.success }}><IconCheck /></span>
              <span style={{ fontSize: 14, fontWeight: 700, color: T.success }}>Agent compiled {ingestionResults.length} product{ingestionResults.length !== 1 ? "s" : ""}</span>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => { setIngestionResults(null); setLookupStatus([]); }} style={{ padding: "8px 18px", borderRadius: T.radiusSm, border: `1px solid ${T.border}`, background: T.white, color: T.textMid, fontSize: 12, cursor: "pointer", fontWeight: 600 }}>Discard</button>
              <button onClick={confirmIngestion} style={{ padding: "8px 18px", borderRadius: T.radiusSm, border: "none", background: T.success, color: T.white, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Add to Database</button>
            </div>
          </div>
          {ingestionResults.map((p: any, i: number) => (
            <div key={i} style={{ padding: 20, background: T.white, border: `1px solid #BBF7D0`, borderRadius: T.radius, boxShadow: T.shadow }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: T.textDark }}>{p.name} {p.strength}</div>
                  <div style={{ fontSize: 12, color: T.textLight, marginTop: 2 }}>{p.manufacturer} - {p.therapeuticClass || "Uncategorized"}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <RiskBadge risk={p.supplyChainRisk} />
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 10, fontSize: 12, color: T.textLight }}>
                <span>API: <strong style={{ color: p.apiSourceCountry === "United States" ? T.success : p.apiSourceCountry === "China" ? T.danger : T.warning }}>{p.apiSourceCountry || "Unknown"}</strong></span>
                <span>FG: <strong style={{ color: p.finishedGoodsMfg === "United States" ? T.success : T.warning }}>{p.finishedGoodsMfg || "Unknown"}</strong></span>
                <span>NDC: {p.ndc?.join(", ") || "-"}</span>
                <span style={{ color: getConfidenceColor(p.confidenceScore) }}>Confidence: {p.confidenceScore}%</span>
              </div>
              {p.originNotes && (
                <div style={{ marginTop: 10, padding: "10px 14px", background: T.warningBg, border: "1px solid #FDE68A", borderRadius: T.radiusSm, fontSize: 12, color: T.textMid, lineHeight: 1.5 }}>
                  <strong style={{ color: T.warning }}>Origin Notes:</strong> {p.originNotes}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
